import React, { useState, useEffect } from 'react';
import '../styles/SourceIcon.css';
import { Gmail, Outlook, Messages, Twilio, X, GHL, FUB, Salesforce, ExternalLink } from '../assets/icons';
import { Claude as ClaudeLogo, ChatGPT as ChatGPTLogo, Gemini as GeminiLogo } from '../assets/images/ai';
import { HugeiconsIcon } from '@hugeicons/react';
import {
    MessageMultiple01Icon,
    Mail01Icon,
    ApiIcon,
    Building03Icon,
    PencilEdit02Icon,
    AiGenerativeIcon
} from '@hugeicons/core-free-icons';
import { colour } from '../styles/GlobalStyles';

/**
 * FaviconImg — renders a website favicon from Google's Favicon API.
 * Falls back to the generic ExternalLink icon on image load error (e.g.
 * CDN hiccup) or when src is absent.
 *
 * Sizing: `size` (default 16) drives the width/height HTML attributes so the
 * favicon matches the centered footprint of our brand SVGs. Contexts that pass
 * a className with its own width/height (e.g. "leads-activity-rail-link-icon")
 * override that via the CSS cascade.
 *
 * `fallbackFill` colours the generic ExternalLink shown on load error — defaults
 * to gray06 for light backgrounds; pass gray00 (white) on dark surfaces such as
 * the image-source badge so the fallback stays legible.
 */
export function FaviconImg({ src, alt, size = 16, className = '', fallbackFill = colour.gray06 }) {
    const [err, setErr] = useState(false);
    // Reset the error flag when src changes — otherwise a recycled instance
    // (e.g. virtualized table row) could keep showing the fallback for a new,
    // valid favicon after an earlier one failed to load.
    useEffect(() => { setErr(false); }, [src]);
    if (!src || err) {
        return <ExternalLink fill={fallbackFill} className={className} />;
    }
    return (
        <img
            src={src}
            alt={alt || ''}
            width={size}
            height={size}
            className={`favicon-img ${className}`.trim()}
            onError={() => setErr(true)}
        />
    );
}

// Single component that renders the right visual for any source profile,
// hiding the brand-SVG vs HugeIcon split from callers. Profiles come from
// resolveLeadSourceProfile in src/lib/leadSource.js.

function SourceIcon({ profile, size = 18, className = '' }) {
    if (!profile) return null;

    const wrapStyle = { width: size, height: size };
    const cls = `source-icon ${className}`.trim();

    switch (profile.iconKey) {
        case 'gmail':
            return <span className={cls} style={wrapStyle}><Gmail className="source-icon-svg" /></span>;
        case 'outlook':
            return <span className={cls} style={wrapStyle}><Outlook className="source-icon-svg" /></span>;
        case 'messages':
            return <span className={cls} style={wrapStyle}><Messages className="source-icon-svg" /></span>;
        case 'twilio':
            return <span className={cls} style={wrapStyle}><Twilio className="source-icon-svg" /></span>;
        case 'x':
            return <span className={cls} style={wrapStyle}><X className="source-icon-svg source-icon-x" /></span>;
        case 'ghl':
            return <span className={cls} style={wrapStyle}><GHL className="source-icon-svg" /></span>;
        case 'fub':
            return <span className={cls} style={wrapStyle}><FUB className="source-icon-svg" /></span>;
        case 'salesforce':
            return <span className={cls} style={wrapStyle}><Salesforce className="source-icon-svg" /></span>;
        case 'sms':
            return <span className={cls} style={wrapStyle}>
                <HugeiconsIcon icon={MessageMultiple01Icon} size={size} color={colour.gray06} strokeWidth={1.8} />
            </span>;
        case 'email-generic':
            return <span className={cls} style={wrapStyle}>
                <HugeiconsIcon icon={Mail01Icon} size={size} color={colour.gray06} strokeWidth={1.8} />
            </span>;
        case 'api':
            return <span className={cls} style={wrapStyle}>
                <HugeiconsIcon icon={ApiIcon} size={size} color={colour.gray06} strokeWidth={1.8} />
            </span>;
        case 'mls':
            return <span className={cls} style={wrapStyle}>
                <HugeiconsIcon icon={Building03Icon} size={size} color={colour.gray06} strokeWidth={1.8} />
            </span>;
        case 'manual':
            return <span className={cls} style={wrapStyle}>
                <HugeiconsIcon icon={PencilEdit02Icon} size={size} color={colour.gray06} strokeWidth={1.8} />
            </span>;
        case 'claude':
            return <span className={cls} style={wrapStyle}><img src={ClaudeLogo} alt="" className="source-icon-svg" /></span>;
        case 'chatgpt':
            return <span className={cls} style={wrapStyle}><img src={ChatGPTLogo} alt="" className="source-icon-svg" /></span>;
        case 'gemini':
            return <span className={cls} style={wrapStyle}><img src={GeminiLogo} alt="" className="source-icon-svg" /></span>;
        case 'ai-agent':
            return <span className={cls} style={wrapStyle}>
                <HugeiconsIcon icon={AiGenerativeIcon} size={size} color={colour.gray06} strokeWidth={1.8} />
            </span>;
        default:
            return <span className={cls} style={wrapStyle}>
                <HugeiconsIcon icon={ApiIcon} size={size} color={colour.gray06} strokeWidth={1.8} />
            </span>;
    }
}

export default SourceIcon;
